import { ScriptAnalysis } from "@/types/analysis";
import { User, UserUpdate } from "@/types/user";
import { InvitationDetails } from "@/types/collaboration";
import { BookingWindow, BookingWindowCreate, BookingWindowUpdate, BookingWindowSummary } from "@/types/booking-window";
import type {
  GoDeeperRequest,
  GoDeeperResponse,
  RecommendationUpdate,
  CompletionMethod
} from '@/types/artie-score';
import { logger } from "@/lib/logger";
import { useAuthStore } from "../stores/authStore";
import { showError } from "@/utils/toast";
import { fetchInventoryMode } from "@/hooks/useInventoryMode";

// API configuration and utilities
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  logger.error("VITE_API_BASE_URL is not defined - API calls will fail", undefined, {
    environment: import.meta.env.MODE,
    availableEnvVars: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
  });
} else {
  logger.info("API configuration loaded", {
    baseUrl: API_BASE_URL,
    environment: import.meta.env.MODE
  });
}

export const API_ENDPOINTS = {
  // Health
  health: "/healthz",

  // Authentication
  auth: {
    config: "/api/v1/auth/config",
    signup: "/api/v1/auth/signup",
    login: "/api/v1/auth/login",
    logout: "/api/v1/auth/logout",
    me: "/api/v1/auth/me",
    forgotPassword: "/api/v1/auth/forgot-password",
    resetPassword: "/api/v1/auth/reset-password",
    profilePhotoInit: "/api/v1/auth/profile-photo/init",
    referralEmail: "/api/v1/auth/referral-email",
  },

  // Projects
  projects: {
    list: "/api/v1/projects",
    create: "/api/v1/projects",
    get: (id: string) => `/api/v1/projects/${id}`,
    update: (id: string) => `/api/v1/projects/${id}`,
    delete: (id: string) => `/api/v1/projects/${id}`,
    uploadRecommendationArtifact: (projectId: string, recommendationId: string) =>
      `/api/v1/projects/${projectId}/recommendations/${recommendationId}/artifact`,
  },

  // Scripts
  scripts: {
    upload: "/api/v1/scripts/upload",
    uploadInit: "/api/v1/scripts/upload/init",
    uploadComplete: (id: string) => `/api/v1/scripts/${id}/upload/complete`,
    uploadLimits: "/api/v1/scripts/upload/limits",
    byProject: (projectId: string) => `/api/v1/scripts/project/${projectId}`,
    get: (id: string) => `/api/v1/scripts/${id}`,
    update: (id: string) => `/api/v1/scripts/${id}`,
    delete: (id: string) => `/api/v1/scripts/${id}`,
    reanalyze: (id: string) => `/api/v1/scripts/${id}/reanalyze`,
    updateQuery: (scriptId: string, queryId: string) =>
      `/api/v1/scripts/${scriptId}/queries/${queryId}`,
    getAnalysis: (id: string) => `/api/v1/scripts/${id}/analysis`,
    updateOpportunity: (scriptId: string, opportunityId: string) =>
      `/api/v1/scripts/${scriptId}/opportunities/${opportunityId}`,
    approveQuery: (scriptId: string, queryId: string) =>
      `/api/v1/scripts/${scriptId}/queries/${queryId}/approve`,
    rejectQuery: (scriptId: string, queryId: string) =>
      `/api/v1/scripts/${scriptId}/queries/${queryId}/reject`,
    preview: (id: string) => `/api/v1/scripts/${id}/preview`,
    budget: (id: string) => `/api/v1/scripts/${id}/budget`,
    statistics: "/api/v1/scripts/statistics/overview",
    extractLogline: (id: string) => `/api/v1/scripts/${id}/extract-logline`,
  },
  analysis: "/api/v1/analysis",
  integrations: {
    youtube: {
      connect: "/api/v1/integrations/youtube/connect",
      finalize: "/api/v1/integrations/youtube/finalize",
      stats: "/api/v1/integrations/youtube/stats",
      disconnect: "/api/v1/integrations/youtube/disconnect",
    },
    channels: {
      all: "/api/v1/integrations/channels/all",
    },
    upload: {
      screenshotInit: "/api/v1/integrations/upload/screenshot/init",
      screenshotComplete: "/api/v1/integrations/upload/screenshot/complete",
    }
  },
  admin: {
    users: "/api/v1/admin/users",
    projects: "/api/v1/admin/projects",
    project: (id: string) => `/api/v1/admin/projects/${id}`,
    deleteProject: (id: string) => `/api/v1/admin/projects/${id}`,
    reviewProject: (id: string) => `/api/v1/admin/projects/${id}/review`,
    scriptDownload: (id: string) => `/api/v1/admin/scripts/${id}/download`,
    pinTactic: (projectId: string) => `/api/v1/admin/projects/${projectId}/tactics/pin`,
    unpinTactic: (projectId: string) => `/api/v1/admin/projects/${projectId}/tactics/unpin`,
    tactics: "/api/v1/admin/tactics",
    tacticStatus: (tacticId: string) => `/api/v1/admin/tactics/${tacticId}/status`,
    overrideTactic: (projectId: string) => `/api/v1/admin/projects/${projectId}/tactics/override`,
    getTacticOverrides: (projectId: string) => `/api/v1/admin/projects/${projectId}/tactics/overrides`,
    setTacticLimit: (projectId: string) => `/api/v1/admin/projects/${projectId}/tactic-limit`,
    // Booking Windows
    bookingWindows: "/api/v1/booking-windows",
    bookingWindow: (id: string) => `/api/v1/booking-windows/${id}`,
    uploadAsset: (projectId: string) => `/api/v1/admin/projects/${projectId}/assets/upload`,
    deleteAsset: (projectId: string, assetId: string) => `/api/v1/admin/projects/${projectId}/assets/${assetId}`,
    uploadAssetOverride: (projectId: string) => `/api/v1/admin/projects/${projectId}/assets/override`,
    deleteAssetOverride: (projectId: string, assetId: string) => `/api/v1/admin/projects/${projectId}/assets/${assetId}/override`,
    uploadScriptOverride: (projectId: string) => `/api/v1/admin/projects/${projectId}/scripts/override`,
    uploadRecommendationArtifactOverride: (projectId: string, recommendationId: string) => `/api/v1/admin/projects/${projectId}/recommendations/${recommendationId}/artifact/override`,
    updateProjectDetailsOverride: (projectId: string) => `/api/v1/admin/projects/${projectId}/details/override`,
    brandPackages: (brandId: string) => `/api/v1/ops-admin/brands/${brandId}/packages`,
  },
  artieScore: {
    calculate: (projectId: string) => `/api/v1/artie-score/mvp/calculate/${projectId}`,
    status: (projectId: string) => `/api/v1/artie-score/mvp/status/${projectId}`,
    getByProject: (projectId: string) => `/api/v1/artie-score/mvp/project/${projectId}`,
    getById: (scoreId: string) => `/api/v1/artie-score/mvp/${scoreId}`,
    delete: (scoreId: string) => `/api/v1/artie-score/mvp/${scoreId}`,
    history: (projectId: string) => `/api/v1/artie-score/mvp/history/${projectId}`,
    acceptRecommendation: (projectId: string) => `/api/v1/artie-score/mvp/project/${projectId}/recommendations/accept`,
    dismissRecommendation: (projectId: string) => `/api/v1/artie-score/mvp/project/${projectId}/recommendations/dismiss`,
    updateRecommendation: (projectId: string, recommendationId: string) => `/api/v1/artie-score/mvp/project/${projectId}/recommendations/${recommendationId}/update`,
    goDeeperChat: (projectId: string, recommendationId: string) => `/api/v1/artie-score/mvp/project/${projectId}/recommendations/${recommendationId}/go-deeper`,
    completeRecommendation: (projectId: string, recommendationId: string) => `/api/v1/artie-score/mvp/project/${projectId}/recommendations/${recommendationId}/complete`,
    recalculate: (projectId: string) => `/api/v1/artie-score/mvp/recalculate/${projectId}`,
    applyBonus: (projectId: string) => `/api/v1/artie-score/mvp/apply-recommendation-bonus/${projectId}`,
    override: (scoreId: string) => `/api/v1/artie-score/${scoreId}/override`,
    // V2 Tactics endpoints
    acceptTactic: (projectId: string) => `/api/v1/artie-score/mvp/project/${projectId}/tactics/accept`,
    declineTactic: (projectId: string) => `/api/v1/artie-score/mvp/project/${projectId}/tactics/decline`,
    updateTactic: (projectId: string, tacticId: string) => `/api/v1/artie-score/mvp/project/${projectId}/tactics/${tacticId}/update`,
    goDeeperTactic: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/go-deeper`,
    getTacticChatHistory: (projectId: string, tacticId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/${tacticId}/chat-history`,
    completeTactic: (projectId: string) => `/api/v1/artie-score/mvp/project/${projectId}/tactics/complete`,
  },
  pitchDeck: {
    generate: "/api/v1/pitch-deck/generate",
    getByProject: (projectId: string) => `/api/v1/pitch-deck/project/${projectId}`,
    regenerateSection: (deckId: string) => `/api/v1/pitch-deck/${deckId}/regenerate-section`,
    updateSection: (deckId: string, sectionType: string) => `/api/v1/pitch-deck/${deckId}/section/${sectionType}`,
    uploadAssetInit: (deckId: string) => `/api/v1/pitch-deck/${deckId}/assets/init`,
    uploadAssetComplete: (deckId: string) => `/api/v1/pitch-deck/${deckId}/assets/complete`,
    setAssetBackground: (deckId: string, assetId: string) => `/api/v1/pitch-deck/${deckId}/assets/${assetId}/set-background`,
    deleteAsset: (deckId: string, assetId: string) => `/api/v1/pitch-deck/${deckId}/assets/${assetId}`,
    updateTemplate: (deckId: string) => `/api/v1/pitch-deck/${deckId}/template`,
    templates: "/api/v1/pitch-deck/templates",
    preview: (deckId: string) => `/api/v1/pitch-deck/${deckId}/preview`,
  },
  share: {
    create: "/api/v1/share/create",
    get: (token: string) => `/api/v1/share/${token}`,
    deactivate: (token: string) => `/api/v1/share/${token}`,
    projectLinks: (projectId: string) => `/api/v1/share/project/${projectId}/links`,
    trackConversion: (token: string) => `/api/v1/share/conversion/${token}`,
  },
  // Early Access
  earlyAccess: {
    create: "/api/v1/early-access",
    update: (id: string) => `/api/v1/early-access/${id}`,
    connectYouTube: "/api/v1/early-access/youtube/connect",
    finalizeYouTube: "/api/v1/early-access/youtube/finalize",
  },
  // Collaboration
  collaboration: {
    addCollaborator: (projectId: string) => `/api/v1/collaboration/projects/${projectId}/collaborators`,
    removeCollaborator: (projectId: string, userId: string) => `/api/v1/collaboration/projects/${projectId}/collaborators/${userId}`,
    updateCollaborator: (projectId: string, userId: string) => `/api/v1/collaboration/projects/${projectId}/collaborators/${userId}`,
    addStickyNote: (projectId: string) => `/api/v1/collaboration/projects/${projectId}/sticky-notes`,
    updateStickyNote: (projectId: string, noteId: string) => `/api/v1/collaboration/projects/${projectId}/sticky-notes/${noteId}`,
    deleteStickyNote: (projectId: string, noteId: string) => `/api/v1/collaboration/projects/${projectId}/sticky-notes/${noteId}`,
    getProjectCollaboration: (projectId: string) => `/api/v1/collaboration/projects/${projectId}`,
    checkPermission: (projectId: string) => `/api/v1/collaboration/projects/${projectId}/permission`,
  },
  // Analytics
  analytics: {
    overview: "/api/v1/analytics/overview",
    project: (projectId: string) => `/api/v1/analytics/projects/${projectId}`,
  },
  // Complete Tactics
  tacticsComplete: {
    markViewed: (projectId: string) => `/api/v1/tactics-complete/projects/${projectId}/tactics/mark-viewed`,
    definitions: "/api/v1/tactics-complete/tactics/definitions",
    goDeeper: (projectId: string, tacticId: string) => `/api/v1/tactics-complete/projects/${projectId}/tactics/${tacticId}/go-deeper`,
    accept: (projectId: string, tacticId: string) => `/api/v1/tactics-complete/projects/${projectId}/tactics/${tacticId}/accept`,
    decline: (projectId: string, tacticId: string) => `/api/v1/tactics-complete/projects/${projectId}/tactics/${tacticId}/decline`,
    complete: (projectId: string, tacticId: string) => `/api/v1/tactics-complete/projects/${projectId}/tactics/${tacticId}/complete`,
  },
  // Tactics V2 endpoints
  tacticsV2: {
    recommendations: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/recommendations`,
    accept: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/accept`,
    decline: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/decline`,
    complete: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/complete`,
    goDeeper: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/go-deeper`,
    markViewed: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/mark-viewed`,
    chatHistory: (projectId: string, tacticId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics/${tacticId}/chat-history`,
    tactics: (projectId: string) => `/api/v1/tactics-v2/projects/${projectId}/tactics`,
    definitions: "/api/v1/tactics-v2/tactics/definitions",
  },
  // Consent
  consent: {
    create: "/api/v1/consent",
    list: "/api/v1/consent",
    summary: "/api/v1/consent/summary",
    validate: "/api/v1/consent/validate",
    get: (consentType: string) => `/api/v1/consent/${consentType}`,
    update: (consentType: string) => `/api/v1/consent/${consentType}`,
    revoke: (consentType: string) => `/api/v1/consent/${consentType}/revoke`,
    bulk: "/api/v1/consent/bulk",
    termsOfService: "/api/v1/consent/terms-of-service",
    privacyPolicy: "/api/v1/consent/privacy-policy",
    ageVerification: "/api/v1/consent/age-verification",
    aiProcessing: "/api/v1/consent/ai-processing",
    dataSharing: "/api/v1/consent/data-sharing",
    youtubeOauth: "/api/v1/consent/youtube-oauth",
    campaignTerms: "/api/v1/consent/campaign-terms",
    producerAuthorization: "/api/v1/consent/producer-authorization",
  },
  // Brand Marketplace
  brandMarketplace: {
    packages: "/api/v1/brand-marketplace/packages",
    package: "/api/v1/brand-marketplace/package",
    packageDetails: (packageId: string) => `/api/v1/brand-marketplace/packages/${packageId}`,
    holdPackage: (packageId: string) => `/api/v1/brand-marketplace/packages/${packageId}/hold`,
    eligibleProjects: "/api/v1/brand-marketplace/eligible-projects",
    requestScriptContext: (campaignId: string, projectId: string) => `/api/v1/brand-marketplace/campaigns/${campaignId}/projects/${projectId}/request-script-context`,
    preferenceSignals: (campaignId: string) => `/api/v1/brand-marketplace/campaigns/${campaignId}/preference-signals`,
  },
} as const;

// API client class
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    console.log(`API Request: ${options.method || 'GET'} ${endpoint}`);

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Safari fallback: Add Authorization header if token exists in sessionStorage
    const safariToken = sessionStorage.getItem('safari_auth_token');
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (safariToken && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${safariToken}`;
      if (isSafari) {
        console.log('🍎 Safari: Using stored token for Authorization header');
      }
    }

    // Handle Content-Type header properly for FormData uploads
    if (headers["Content-Type"] === null) {
      // Remove Content-Type entirely for FormData - let browser set multipart/form-data boundary
      delete headers["Content-Type"];
    } else if (!headers.hasOwnProperty("Content-Type")) {
      // Set default Content-Type for regular JSON requests
      headers["Content-Type"] = "application/json";
    }
    // If Content-Type is explicitly set to a string value, keep it as-is

    const config: RequestInit = {
      ...options,
      headers,
      credentials: "include" // Send cookies with requests
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 204) {
        return {} as T;
      }

      // For file downloads, we need to handle the response differently
      const contentType = response.headers.get("content-type");
      if (contentType && (
        contentType.includes("application/vnd.openxmlformats-officedocument.presentationml.presentation") ||
        contentType.includes("application/pdf") ||
        contentType.includes("text/plain") ||
        contentType.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
        contentType.includes("application/octet-stream")
      )) {
        if (!response.ok) {
          // Try to parse error JSON even from blob responses
          try {
            const errorBlob = await response.blob();
            const errorText = await errorBlob.text();
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.detail || `HTTP ${response.status}`);
          } catch (e) {
            throw new Error(`HTTP ${response.status}`);
          }
        }
        return response.blob() as Promise<T>;
      }

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Check if the request was for the /me endpoint or another sensitive one
          const isAuthEndpoint = endpoint.includes(API_ENDPOINTS.auth.me) || endpoint.includes('/api/v1/admin');

          if (isAuthEndpoint) {
            // Prevent logout loops if logout itself fails
            if (!endpoint.includes(API_ENDPOINTS.auth.logout)) {
              try {
                showError("Session expired. Please log in again.");
                // Perform a full logout
                await this.logout();
                useAuthStore.getState().logout();

                // Dispatch a custom event to notify the app to redirect
                window.dispatchEvent(new CustomEvent("session-expired"));
              } catch (logoutError) {
                console.error("Error during logout after 401:", logoutError);
              }
            }
            // Throw a specific error to stop further processing
            throw new Error("Session expired. Redirecting to login.");
          }
        } else if (response.status === 413) {
          // Handle file size limit errors specifically
          throw new Error((data as any).detail || "File size exceeds maximum allowed limit. Please use a smaller file.");
        } else if (response.status === 422) {
          const errorDetail = (data as any).detail;
          if (Array.isArray(errorDetail)) {
            // FIX: Improved error message extraction for 422 Validation
            const fieldErrors = errorDetail
              .map((err: any) => `${err.loc?.slice(1).join(".")} - ${err.msg}`) // Slice(1) removes 'body' prefix
              .join(", ");
            throw new Error(`Validation error: ${fieldErrors}`);
          }
          throw new Error((data as any).detail || "Invalid data provided");
        } else if (response.status === 500) {
          throw new Error("Server error. Please try again later.");
        }
        throw new Error((data as any).detail || (data as any).message || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error("Network error occurred");
    }
  }

  // Health check
  async healthCheck() {
    return this.request("/healthz");
  }

  // Authentication
  async getAuthConfig() {
    return this.request<{ google_client_id: string; turnstile_site_key?: string | null }>(API_ENDPOINTS.auth.config);
  }

  async signup(userData: {
    name: string;
    email: string;
    password: string;
    referral_code?: string;
    turnstile_token?: string;
    website?: string; // honeypot, always empty for a person
  }) {
    return this.request(API_ENDPOINTS.auth.signup, {
      method: "POST",
      body: JSON.stringify({
        ...userData,
        email: userData.email.toLowerCase(),
      }),
    });
  }

  async login(credentials: { email: string; password: string }) {
    const response = await this.request<{
      message: string;
      access_token?: string;
      safari_compatible?: boolean;
      debug_info?: {
        is_safari: boolean;
        cookie_set: boolean;
        token_provided: boolean;
        production?: boolean;
      }
    }>(
      API_ENDPOINTS.auth.login,
      {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          username: credentials.email.toLowerCase(),
          password: credentials.password,
        }).toString(),
      }
    );

    // Store token for Safari if provided
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari && response.access_token) {
      console.log("🍎 Safari: Storing auth token from login response");
      sessionStorage.setItem('safari_auth_token', response.access_token);
    }

    return response;
  }

  async googleLogin(code?: string, referralCode?: string, accessToken?: string, role?: string) {
    const response = await this.request<{
      message: string;
      access_token?: string;
      safari_compatible?: boolean;
      debug_info?: {
        is_safari: boolean;
        cookie_set: boolean;
        token_provided: boolean;
        production?: boolean;
      }
    }>(
      "/api/v1/auth/google",
      {
        method: "POST",
        body: JSON.stringify({ code, access_token: accessToken, referral_code: referralCode, role }),
      }
    );

    // Store token for Safari if provided
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari && response.access_token) {
      console.log("🍎 Safari: Storing auth token from Google login response");
      sessionStorage.setItem('safari_auth_token', response.access_token);
    }

    return response;
  }

  async logout() {
    // Clear Safari fallback token
    sessionStorage.removeItem('safari_auth_token');
    return this.request(API_ENDPOINTS.auth.logout, { method: "POST" });
  }

  async getCurrentUser() {
    return this.request<User>(API_ENDPOINTS.auth.me);
  }

  async getBrandCurrentUser() {
    return this.request<{
      user: User;
      brand: any;
      onboarding_status: string;
      onboarding_complete: boolean;
      progress_percentage: number;
      next_step: string;
    }>("/api/v1/brands/me/profile");
  }

  async updateCurrentUser(userData: UserUpdate) {
    return this.request<User>(API_ENDPOINTS.auth.me, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  async requestPasswordReset(email: string) {
    return this.request(API_ENDPOINTS.auth.forgotPassword, {
      method: "POST",
      body: JSON.stringify({ email: email.toLowerCase() }),
    });
  }

  async resetPassword(token: string, newPassword: string) {
    return this.request(API_ENDPOINTS.auth.resetPassword, {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async uploadProfilePhoto(file: File): Promise<string> {
    // 1. Init
    const initRes = await this.request<{ upload_url: string; fields: Record<string, string>; s3_key: string }>(
      API_ENDPOINTS.auth.profilePhotoInit,
      {
        method: "POST",
        body: JSON.stringify({ filename: file.name, file_type: file.type || "image/jpeg" }),
      }
    );

    // 2. S3 Upload
    const s3FormData = new FormData();
    Object.entries(initRes.fields).forEach(([key, value]) => {
      s3FormData.append(key, value as string);
    });
    s3FormData.append("file", file);

    // We use mode: 'no-cors' to bypass the browser's CORS restriction.
    await fetch(initRes.upload_url, {
      method: "POST",
      body: s3FormData,
      mode: 'no-cors',
    });

    return initRes.s3_key;
  }

  async sendReferralEmail(data: { recipient_name: string; recipient_email: string; referral_link: string }) {
    return this.request(API_ENDPOINTS.auth.referralEmail, {
      method: "POST",
      body: JSON.stringify({
        ...data,
        recipient_email: data.recipient_email.toLowerCase(),
      }),
    });
  }

  // Projects
  async createProject(projectData: any) {
    return this.request(API_ENDPOINTS.projects.create, {
      method: "POST",
      body: JSON.stringify(projectData),
    });
  }

  async getProjects(page = 1, limit = 20, search?: string, status?: string, sort?: string) {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);
    if (sort) params.append('sort', sort);

    return this.request<ProjectListResponse>(`${API_ENDPOINTS.projects.list}?${params.toString()}`);
  }

  async getProject(id: string) {
    return this.request<Project>(API_ENDPOINTS.projects.get(id));
  }

  async updateProject(id: string, projectData: any) {
    return this.request(API_ENDPOINTS.projects.update(id), {
      method: "PUT",
      body: JSON.stringify(projectData),
    });
  }

  async deleteProject(id: string) {
    return this.request(API_ENDPOINTS.projects.delete(id), { method: "DELETE" });
  }

  async updateProductionPlan(projectId: string, productionPlan: any) {
    return this.request(`/api/v1/projects/${projectId}/production-plan`, {
      method: "PUT",
      body: JSON.stringify(productionPlan),
    });
  }

  async updateVerification(projectId: string, verification: any) {
    return this.request(`/api/v1/projects/${projectId}/verification`, {
      method: "PUT",
      body: JSON.stringify(verification),
    });
  }

  async addTeamMember(projectId: string, teamMember: any) {
    return this.request(`/api/v1/projects/${projectId}/team-member`, {
      method: "POST",
      body: JSON.stringify(teamMember),
    });
  }

  async addBrandWork(projectId: string, brandWork: any) {
    return this.request(`/api/v1/projects/${projectId}/brand-work`, {
      method: "POST",
      body: JSON.stringify(brandWork),
    });
  }

  // -------- Scripts (TEXT submission supported) --------

  async getScriptsByProject(projectId: string): Promise<Script[]> {
    return this.request(API_ENDPOINTS.scripts.byProject(projectId));
  }

  async getUploadLimits(): Promise<{
    max_file_size_bytes: number;
    max_file_size_mb: number;
    supported_file_types: string[];
    supported_extensions: string[];
  }> {
    return this.request(API_ENDPOINTS.scripts.uploadLimits);
  }

  async uploadScript(params: {
    projectId: string;
    title?: string;
    content?: string;
    file?: File;
    notes?: string;
    and_calculate_score?: boolean;
  }): Promise<Script> {
    const { projectId, title, content, file, and_calculate_score } = params;

    // Allow empty projectId for pending uploads (file only)
    if (!projectId && !file) {
      throw new Error("Client Error: Project ID is missing from upload payload.");
    }

    // Handle File Upload via S3 Pre-signed URL
    if (file) {
      try {
        // Client-side file size validation
        const limits = await this.getUploadLimits();
        if (file.size > limits.max_file_size_bytes) {
          throw new Error(
            `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size of ${limits.max_file_size_mb}MB. Please use a smaller file.`
          );
        }

        // File type validation
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!limits.supported_extensions.includes(fileExtension)) {
          throw new Error(
            `File type not supported. Please use one of: ${limits.supported_extensions.join(', ')}`
          );
        }
        // 1. Initiate Upload
        const initBody = {
          project_id: projectId,
          filename: file.name,
          file_size: file.size,
          file_type: file.type || "application/octet-stream",
          title: title || file.name
        };

        const initRes = await this.request<{ script_id: string, upload_url: string, fields: Record<string, string> }>(
          API_ENDPOINTS.scripts.uploadInit,
          {
            method: "POST",
            body: JSON.stringify(initBody),
          }
        );

        // 2. Upload to S3 directly
        const s3FormData = new FormData();
        // Append fields required by S3 (must be in correct order, usually before file)
        Object.entries(initRes.fields).forEach(([key, value]) => {
          s3FormData.append(key, value as string);
        });
        s3FormData.append("file", file);

        // We use mode: 'no-cors' to bypass the browser's CORS restriction.
        // The upload will succeed (Server returns 204), but the browser would normally block
        // the response due to missing CORS headers. 'no-cors' ignores the response reading.
        await fetch(initRes.upload_url, {
          method: "POST",
          body: s3FormData,
          mode: 'no-cors',
        });

        // 3. Complete Upload (Notify Backend)
        const completeUrl = API_ENDPOINTS.scripts.uploadComplete(initRes.script_id) + (and_calculate_score ? "?and_calculate_score=true" : "");
        const completeRes = await this.request<{ script_id: string, message: string }>(
          completeUrl,
          { method: "POST" }
        );

        // Return a mock Script object with the ID to satisfy interface
        // Ideally, we should fetch the full script here, but for performance we'll return the ID
        return {
          _id: completeRes.script_id,
          id: completeRes.script_id,
          script_id: completeRes.script_id,
          project_id: projectId,
          user_id: "", // Placeholder
          filename: file.name,
          file_size: file.size,
          file_type: file.type,
          status: "processing",
          commercial_queries: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as unknown as Script;

      } catch (error) {
        console.error("S3 Upload Error:", error);
        throw error;
      }
    }

    // Handle Text Upload (Legacy Flow)
    const formData = new FormData();
    formData.append("project_id", projectId);

    if (title) formData.append("title", title);
    if (content) formData.append("script_text", content);

    return this.request(API_ENDPOINTS.scripts.upload, {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any },
    });
  }

  async updateScript(id: string, updates: Partial<Script> | any, and_calculate_score = false): Promise<Script> {
    const url = API_ENDPOINTS.scripts.update(id) + (and_calculate_score ? "?and_calculate_score=true" : "");
    return this.request(url, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteScript(id: string): Promise<void> {
    return this.request(API_ENDPOINTS.scripts.delete(id), { method: "DELETE" });
  }

  async reanalyzeScript(id: string, formData: FormData): Promise<Script> {
    return this.request(API_ENDPOINTS.scripts.reanalyze(id), {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any }, // let browser set multipart/form-data
    });
  }

  async updateCommercialQuery(
    scriptId: string,
    queryId: string,
    updateData: Partial<CommercialQuery>
  ): Promise<Script> {
    return this.request(API_ENDPOINTS.scripts.updateQuery(scriptId, queryId), {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
  }

  async approveCommercialQuery(scriptId: string, queryId: string): Promise<Script> {
    return this.request(API_ENDPOINTS.scripts.approveQuery(scriptId, queryId), { method: "POST" });
  }

  async rejectCommercialQuery(scriptId: string, queryId: string): Promise<Script> {
    return this.request(API_ENDPOINTS.scripts.rejectQuery(scriptId, queryId), { method: "POST" });
  }

  async getScriptPreview(id: string): Promise<ScriptPreview> {
    return this.request(API_ENDPOINTS.scripts.preview(id));
  }

  async getScriptBudget(id: string): Promise<ScriptBudget> {
    return this.request(API_ENDPOINTS.scripts.budget(id));
  }

  async getScriptStatistics(): Promise<ScriptStatistics> {
    return this.request(API_ENDPOINTS.scripts.statistics);
  }

  async getScript(id: string): Promise<Script> {
    return this.request(API_ENDPOINTS.scripts.get(id));
  }

  async getScriptAnalysis(id: string): Promise<ScriptAnalysis> {
    return this.request(API_ENDPOINTS.scripts.getAnalysis(id));
  }

  async updateBrandOpportunity(
    scriptId: string,
    opportunityId: string,
    status: "pending" | "accepted" | "rejected"
  ): Promise<void> {
    return this.request(API_ENDPOINTS.scripts.updateOpportunity(scriptId, opportunityId), {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  }

  async analyzeScript(projectContext: any, file?: File, scriptContent?: string): Promise<any> {
    const formData = new FormData();
    formData.append("project_context", JSON.stringify(projectContext));
    if (file) formData.append("file", file);
    if (scriptContent) formData.append("script_content", scriptContent);

    return this.request(API_ENDPOINTS.analysis, {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any }, // let browser set multipart/form-data
    });
  }

  async extractLogline(scriptId: string): Promise<{ logline: string; script_id: string }> {
    return this.request(API_ENDPOINTS.scripts.extractLogline(scriptId), {
      method: "POST",
    });
  }

  // Integrations
  async connectYouTube() {
    return this.request<{ url: string }>(API_ENDPOINTS.integrations.youtube.connect);
  }

  async finalizeYouTubeConnection(code: string) {
    return this.request(
      `${API_ENDPOINTS.integrations.youtube.finalize}?code=${code}`,
      { method: "POST" }
    );
  }

  async getYouTubeStats() {
    return this.request(API_ENDPOINTS.integrations.youtube.stats);
  }

  async getYouTubeAnalytics(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    return this.request(`/api/v1/integrations/youtube/analytics?${params.toString()}`);
  }

  async disconnectYouTube() {
    return this.request(API_ENDPOINTS.integrations.youtube.disconnect, {
      method: "DELETE",
    });
  }

  async getAllChannels() {
    return this.request<{ channels: any[], total_count: number }>(
      API_ENDPOINTS.integrations.channels.all
    );
  }

  async initScreenshotUpload(filename: string, fileType: string) {
    return this.request<{ upload_url: string; fields: Record<string, string>; s3_key: string }>(
      API_ENDPOINTS.integrations.upload.screenshotInit,
      {
        method: "POST",
        body: JSON.stringify({ filename, file_type: fileType }),
      }
    );
  }

  async completeScreenshotUpload(s3Key: string, platform: string) {
    return this.request<{ message: string; data: any }>(
      API_ENDPOINTS.integrations.upload.screenshotComplete,
      {
        method: "POST",
        body: JSON.stringify({ s3_key: s3Key, platform }),
      }
    );
  }

  // Admin
  async getAdminUsers(page = 1, limit = 10, search = "") {
    const skip = (page - 1) * limit;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      ...(search && { search }),
    }).toString();

    return this.request<{ items: User[]; total: number; page: number; limit: number }>(
      `${API_ENDPOINTS.admin.users}?${query}`
    );
  }

  async getAdminProjects(
    page = 1,
    limit = 10,
    search = "",
    reviewStatus = "all",
    reachStatus = "all",
    brandFitStatus = "all",
    productionStatus = "all"
  ) {
    const skip = (page - 1) * limit;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      ...(search && { search }),
      ...(reviewStatus !== "all" && { review_status: reviewStatus }),
      ...(reachStatus !== "all" && { reach_status: reachStatus }),
      ...(brandFitStatus !== "all" && { brand_fit_status: brandFitStatus }),
      ...(productionStatus !== "all" && { production_status: productionStatus }),
    }).toString();

    return this.request<{ items: any[]; total: number; page: number; limit: number }>(
      `${API_ENDPOINTS.admin.projects}?${query}`
    );
  }

  async getNextPendingProject(currentProjectId?: string) {
    const query = currentProjectId ? `?current_project_id=${currentProjectId}` : "";
    return this.request<{ project_id: string } | null>(
      `/api/v1/admin/projects/next-pending${query}`
    );
  }

  async getAdminEarlyAccessUsers(page = 1, limit = 10, search = "") {
    const skip = (page - 1) * limit;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      ...(search && { search }),
    }).toString();

    return this.request<{ items: any[]; total: number; page: number; limit: number }>(
      `/api/v1/admin/early-access?${query}`
    );
  }
  async getAdminProject(projectId: string) {
    return this.request<any>(API_ENDPOINTS.admin.project(projectId));
  }

  async deleteProjectAdmin(projectId: string) {
    return this.request(API_ENDPOINTS.admin.deleteProject(projectId), {
      method: "DELETE",
    });
  }

  async toggleProjectReviewStatus(projectId: string, isReviewed: boolean) {
    return this.request(
      `${API_ENDPOINTS.admin.reviewProject(projectId)}?is_reviewed=${isReviewed}`,
      {
        method: "PATCH",
      }
    );
  }

  async downloadAdminScript(scriptId: string, inline: boolean = false): Promise<Blob> {
    const url = `${API_ENDPOINTS.admin.scriptDownload(scriptId)}${inline ? '?inline=true' : ''}`;

    try {
      // First try with the standard request method (uses cookies)
      return await this.request<Blob>(url);
    } catch (error) {
      // If cookie auth fails in production, provide helpful error message
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Admin authentication failed. Please log in again with admin credentials.');
      }
      throw error;
    }
  }

  async regenerateScore(projectId: string) {
    return this.request(
      `/api/v1/admin/projects/${projectId}/regenerate-score`,
      {
        method: "POST",
      }
    );
  }
  async pinTactic(projectId: string, tacticId: string, rank: number, reason?: string) {
    return this.request(API_ENDPOINTS.admin.pinTactic(projectId), {
      method: "POST",
      body: JSON.stringify({ tactic_id: tacticId, rank, reason }),
    });
  }

  async unpinTactic(projectId: string, tacticId: string) {
    return this.request(API_ENDPOINTS.admin.unpinTactic(projectId), {
      method: "POST",
      body: JSON.stringify({ tactic_id: tacticId }),
    });
  }

  async getAllTactics(page = 1, limit = 10, search = "", dimension = "all", status = "all") {
    const skip = (page - 1) * limit;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      ...(search && { search }),
      ...(dimension !== "all" && { dimension }),
      ...(status !== "all" && { status }),
    }).toString();

    return this.request<{ items: any[]; total: number; page: number; limit: number }>(
      `${API_ENDPOINTS.admin.tactics}?${query}`
    );
  }

  async setTacticStatus(tacticId: string, isActive: boolean) {
    return this.request(API_ENDPOINTS.admin.tacticStatus(tacticId), {
      method: "PUT",
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async overrideProjectTactic(projectId: string, tacticId: string, overrideData: { custom_wording?: string; is_hidden?: boolean }) {
    return this.request(API_ENDPOINTS.admin.overrideTactic(projectId), {
      method: "POST",
      body: JSON.stringify({ tactic_id: tacticId, ...overrideData }),
    });
  }

  async uploadProjectAssetOverride(projectId: string, file: File, reason: string): Promise<ProjectAsset> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("reason", reason);

    return this.request(API_ENDPOINTS.admin.uploadAssetOverride(projectId), {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any },
    });
  }

  async deleteProjectAssetOverride(projectId: string, assetId: string, reason: string): Promise<void> {
    return this.request(`${API_ENDPOINTS.admin.deleteAssetOverride(projectId, assetId)}?reason=${encodeURIComponent(reason)}`, {
      method: "DELETE",
    });
  }

  async uploadProjectScriptOverride(projectId: string, file: File, reason: string): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("reason", reason);

    return this.request(API_ENDPOINTS.admin.uploadScriptOverride(projectId), {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any },
    });
  }

  async uploadRecommendationArtifactOverride(projectId: string, recommendationId: string, file: File, reason: string): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("reason", reason);

    return this.request(API_ENDPOINTS.admin.uploadRecommendationArtifactOverride(projectId, recommendationId), {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any },
    });
  }

  async uploadRecommendationArtifact(projectId: string, recommendationId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request(API_ENDPOINTS.projects.uploadRecommendationArtifact(projectId, recommendationId), {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any },
    });
  }

  async updateProjectDetailsOverride(projectId: string, data: { budget?: number, distribution_strategy?: string[], reason: string }): Promise<any> {
    return this.request(API_ENDPOINTS.admin.updateProjectDetailsOverride(projectId), {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getProjectTacticOverrides(projectId: string): Promise<any[]> {
    return this.request(API_ENDPOINTS.admin.getTacticOverrides(projectId));
  }

  async setProjectTacticLimit(projectId: string, limit: number) {
    return this.request(API_ENDPOINTS.admin.setTacticLimit(projectId), {
      method: "PUT",
      body: JSON.stringify({ tactic_limit: limit }),
    });
  }

  // Admin - Booking Windows
  async getAdminBookingWindows(status?: string, search = "", skip = 0, limit = 10) {
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      show_all: "true", // Admin should see all booking windows regardless of start date
    });
    if (status && status !== 'all') params.append("status", status);
    if (search) params.append("search", search);

    return this.request<{ items: BookingWindow[]; total: number; page: number; limit: number }>(`${API_ENDPOINTS.admin.bookingWindows}?${params.toString()}`);
  }

  async getAdminBookingWindow(id: string) {
    return this.request<BookingWindow>(API_ENDPOINTS.admin.bookingWindow(id));
  }

  async createBookingWindow(data: BookingWindowCreate) {
    return this.request<BookingWindow>(API_ENDPOINTS.admin.bookingWindows, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateBookingWindow(id: string, data: BookingWindowUpdate) {
    return this.request<BookingWindow>(API_ENDPOINTS.admin.bookingWindow(id), {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteBookingWindow(id: string) {
    return this.request(API_ENDPOINTS.admin.bookingWindow(id), {
      method: "DELETE",
    });
  }

  async uploadProjectAsset(projectId: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);

    return this.request<ProjectAsset>(API_ENDPOINTS.admin.uploadAsset(projectId), {
      method: "POST",
      body: formData,
      headers: { "Content-Type": null as any },
    });
  }

  async deleteProjectAsset(projectId: string, assetId: string) {
    return this.request(API_ENDPOINTS.admin.deleteAsset(projectId, assetId), {
      method: "DELETE",
    });
  }

  async getBrandPackages(brandId: string, status: string = "active") {
    const params = new URLSearchParams();
    if (status && status !== "all") {
      params.append("status", status);
    }

    return this.request<{ packages: any[] }>(`${API_ENDPOINTS.admin.brandPackages(brandId)}?${params.toString()}`);
  }

  // Artie Score MVP
  async calculateArtieScore(projectId: string, forceRecalculate = false) {
    // REFUSED IN INVENTORY MODE. This is the last of three choke points, not a duplicate of them:
    // useInventoryMode is the fact, use-artie-score stops the twelve screens that read a score
    // through it, and this stops the POST itself — which four callers make directly and would
    // otherwise slip past both (RecommendationsSection, ArtieScorePage, OnboardingStep4, and
    // NewProjectStep3, the last of which is in the live new-project wizard).
    //
    // The backend already declines the work, so the POST is not dangerous — it is dishonest. It
    // returns as though a calculation started, the screen then waits for a score, and none is coming.
    if (await fetchInventoryMode()) {
      return { status: "skipped", reason: "inventory_mode", project_id: projectId };
    }
    const url = `${API_ENDPOINTS.artieScore.calculate(projectId)}${forceRecalculate ? "?force_recalculate=true" : ""}`;
    // The request method automatically handles non-200 responses by throwing an error with the message
    // including our new "Script Validation Failed: ..." message from the backend
    return this.request(url, {
      method: "POST",
    });
  }

  async getArtieScoreStatus(projectId: string) {
    return this.request(API_ENDPOINTS.artieScore.status(projectId));
  }

  async acceptArtieRecommendation(projectId: string, recommendationKey: string) {
    return this.request(
      API_ENDPOINTS.artieScore.acceptRecommendation(projectId),
      {
        method: "POST",
        body: JSON.stringify({ recommendation_key: recommendationKey }),
      }
    );
  }

  async dismissArtieRecommendation(projectId: string, recommendationKey: string) {
    return this.request(
      API_ENDPOINTS.artieScore.dismissRecommendation(projectId),
      {
        method: "POST",
        body: JSON.stringify({ recommendation_key: recommendationKey }),
      }
    );
  }

  async getArtieScoreByProject(projectId: string, options: RequestInit = {}) {
    return this.request(API_ENDPOINTS.artieScore.getByProject(projectId), options);
  }

  async getArtieScoreById(scoreId: string) {
    return this.request(API_ENDPOINTS.artieScore.getById(scoreId));
  }

  async deleteArtieScore(scoreId: string) {
    return this.request(API_ENDPOINTS.artieScore.delete(scoreId), {
      method: "DELETE",
    });
  }

  async getArtieScoreHistory(projectId: string) {
    return this.request(API_ENDPOINTS.artieScore.history(projectId));
  }

  async recalculateArtieScore(projectId: string) {
    // Same refusal as calculateArtieScore — this is the other POST that starts scoring work, reached
    // from the "Recalculate" buttons via use-artie-score's refetch().
    if (await fetchInventoryMode()) {
      return { status: "skipped", reason: "inventory_mode", project_id: projectId };
    }
    // Recalculate returns a status response like calculate, not the full score
    return this.request(API_ENDPOINTS.artieScore.recalculate(projectId), {
      method: "POST",
    });
  }

  async applyRecommendationBonus(projectId: string) {
    return this.request(API_ENDPOINTS.artieScore.applyBonus(projectId), {
      method: "POST",
    });
  }

  async updateRecommendation(projectId: string, update: RecommendationUpdate) {
    return this.request(
      API_ENDPOINTS.artieScore.updateRecommendation(projectId, update.recommendation_id),
      {
        method: "POST",
        body: JSON.stringify(update),
      }
    );
  }

  // V2 Tactics methods
  async acceptTacticRecommendation(projectId: string) {
    return this.request(API_ENDPOINTS.artieScore.acceptTactic(projectId), {
      method: "POST",
    });
  }

  async declineTacticRecommendation(projectId: string) {
    return this.request(API_ENDPOINTS.artieScore.declineTactic(projectId), {
      method: "POST",
    });
  }

  async updateTacticRecommendation(projectId: string, tacticId: string, update: RecommendationUpdate) {
    return this.request(
      API_ENDPOINTS.artieScore.updateTactic(projectId, tacticId),
      {
        method: "POST",
        body: JSON.stringify(update),
      }
    );
  }

  async goDeeperTacticChat(projectId: string, request: GoDeeperRequest) {
    // Convert recommendation_id to tactic_id for V2 tactics endpoint
    const tacticRequest = {
      tactic_id: request.recommendation_id,
      message: request.message
    };

    return this.request<GoDeeperResponse>(
      API_ENDPOINTS.artieScore.goDeeperTactic(projectId),
      {
        method: "POST",
        body: JSON.stringify(tacticRequest),
      }
    );
  }

  async getTacticChatHistory(projectId: string, tacticId: string) {
    return this.request<{
      tactic_id: string;
      chat_messages: Array<{
        role: string;
        content: string;
        timestamp: string;
      }>;
      token_usage: number;
    }>(
      API_ENDPOINTS.artieScore.getTacticChatHistory(projectId, tacticId)
    );
  }

  async completeTacticRecommendation(
    projectId: string,
    completionMethod: CompletionMethod,
    artifactUrls?: string[],
    planDescription?: string
  ) {
    const params = new URLSearchParams({
      completion_method: completionMethod,
    });

    return this.request(
      `${API_ENDPOINTS.artieScore.completeTactic(projectId)}?${params.toString()}`,
      {
        method: "POST",
        body: JSON.stringify({
          artifact_urls: artifactUrls,
          plan_description: planDescription,
        }),
      }
    );
  }

  async overrideArtieScore(scoreId: string, overrideData: {
    artie_score?: number;
    reach_status?: string;
    brand_fit_status?: string;
    production_readiness_status?: string;
    override_reason: string;
  }) {
    return this.request(
      API_ENDPOINTS.artieScore.override(scoreId),
      {
        method: "PUT",
        body: JSON.stringify(overrideData),
      }
    );
  }

  async goDeeperChat(projectId: string, request: GoDeeperRequest) {
    return this.request<GoDeeperResponse>(
      API_ENDPOINTS.artieScore.goDeeperChat(projectId, request.recommendation_id),
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );
  }

  async completeRecommendation(
    projectId: string,
    recommendationId: string,
    completionMethod: CompletionMethod,
    artifactUrls?: string[],
    planDescription?: string
  ) {
    const params = new URLSearchParams({
      completion_method: completionMethod,
    });

    return this.request(
      `${API_ENDPOINTS.artieScore.completeRecommendation(projectId, recommendationId)}?${params.toString()}`,
      {
        method: "POST",
        body: JSON.stringify({
          artifact_urls: artifactUrls,
          plan_description: planDescription,
        }),
      }
    );
  }

  // Pitch Deck
  async generatePitchDeck(data: {
    project_id: string;
    target_audience: "producers" | "brands" | "investors";
    include_sections?: string[];
  }) {
    return this.request<{ id: string; status: string; message: string }>(
      API_ENDPOINTS.pitchDeck.generate,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  }

  async getPitchDeckByProject(projectId: string) {
    return this.request(API_ENDPOINTS.pitchDeck.getByProject(projectId));
  }

  async regenerateDeckSection(deckId: string, sectionType: string) {
    return this.request<PitchDeckSection>(API_ENDPOINTS.pitchDeck.regenerateSection(deckId), {
      method: "POST",
      body: JSON.stringify({ section_type: sectionType }),
    });
  }

  async updateDeckSection(deckId: string, sectionType: string, content: string) {
    return this.request(API_ENDPOINTS.pitchDeck.updateSection(deckId, sectionType), {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  }

  async uploadPitchDeckAsset(deckId: string, file: File, label: string, assetType: string) {
    // 1. Init
    const initRes = await this.request<{ upload_url: string; fields: Record<string, string>; asset_id: string }>(
      API_ENDPOINTS.pitchDeck.uploadAssetInit(deckId),
      {
        method: "POST",
        body: new URLSearchParams({
          filename: file.name,
          file_type: file.type || "application/octet-stream",
        }),
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
      }
    );

    // 2. S3 Upload
    const s3FormData = new FormData();
    Object.entries(initRes.fields).forEach(([key, value]) => {
      s3FormData.append(key, value as string);
    });
    s3FormData.append("file", file);

    await fetch(initRes.upload_url, {
      method: "POST",
      body: s3FormData,
      mode: 'no-cors',
    });

    // 3. Complete
    const completeRes = await this.request(
      API_ENDPOINTS.pitchDeck.uploadAssetComplete(deckId),
      {
        method: "POST",
        body: new URLSearchParams({
          asset_id: initRes.asset_id,
          filename: file.name,
          file_type: file.type || "application/octet-stream",
          label: label,
          asset_type: assetType
        }),
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
      }
    );

    return completeRes;
  }

  async setDeckBackground(deckId: string, assetId: string) {
    return this.request(API_ENDPOINTS.pitchDeck.setAssetBackground(deckId, assetId), {
      method: "POST",
    });
  }

  async deleteDeckAsset(deckId: string, assetId: string) {
    return this.request(API_ENDPOINTS.pitchDeck.deleteAsset(deckId, assetId), {
      method: "DELETE",
    });
  }

  async updateDeckTemplate(deckId: string, template: string) {
    return this.request(API_ENDPOINTS.pitchDeck.updateTemplate(deckId), {
      method: "PUT",
      body: JSON.stringify({ template }),
    });
  }

  async getPitchDeckTemplates() {
    return this.request<Record<string, any>>(API_ENDPOINTS.pitchDeck.templates);
  }

  async getPitchDeckPreview(deckId: string, template: string) {
    return this.request<any[]>(`${API_ENDPOINTS.pitchDeck.preview(deckId)}?template=${template}`);
  }

  async exportPitchDeck(deckId: string, format: 'ppt' | 'pdf'): Promise<Blob> {
    const url = `${this.baseUrl}/api/v1/pitch-deck/${deckId}/export/${format}`;

    // Use a direct fetch call to handle the blob response
    const response = await fetch(url, {
      credentials: 'include' // This is crucial for sending cookies
    });

    if (!response.ok) {
      // Try to parse the error message if the response is JSON
      try {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Export failed with status: ${response.status}`);
      } catch (e) {
        throw new Error(`Export failed with status: ${response.status}`);
      }
    }

    return response.blob();
  }

  // Share Links
  async createShareLink(projectId: string, expiresInDays?: number) {
    return this.request<ShareLinkResponse>(API_ENDPOINTS.share.create, {
      method: "POST",
      body: JSON.stringify({
        project_id: projectId,
        expires_in_days: expiresInDays,
      }),
    });
  }

  async getSharedScore(token: string) {
    return this.request<PublicScorePreview>(API_ENDPOINTS.share.get(token));
  }

  async deactivateShareLink(token: string) {
    return this.request(API_ENDPOINTS.share.deactivate(token), {
      method: "DELETE",
    });
  }

  async getProjectShareLinks(projectId: string) {
    return this.request<{ links: ShareLink[]; total: number }>(
      API_ENDPOINTS.share.projectLinks(projectId)
    );
  }

  async trackShareConversion(token: string) {
    return this.request(API_ENDPOINTS.share.trackConversion(token), {
      method: "POST",
    });
  }

  // Early Access
  async createEarlyAccessUser(data: { name: string; email: string; referral_code?: string }) {
    return this.request<{ _id: string; name: string; email: string; referral_code?: string }>(
      API_ENDPOINTS.earlyAccess.create,
      {
        method: "POST",
        body: JSON.stringify({
          ...data,
          email: data.email.toLowerCase(),
        }),
      }
    );
  }

  async updateEarlyAccessUser(id: string, data: {
    company?: string;
    company_name?: string;
    user_type?: string;
    referral_source?: string;
    role?: string;
    interest_description?: string;
    youtube_channel_id?: string;
    youtube_oauth_connected?: boolean;
    youtube_subscribers?: number;
    youtube_avg_views?: number;
    youtube_data?: any;
    imdb_url?: string;
    tiktok_handle?: string;
    instagram_handle?: string;
    project_description?: string;
  }) {
    return this.request(API_ENDPOINTS.earlyAccess.update(id), {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async connectEarlyAccessYouTube() {
    return this.request<{ url: string }>(API_ENDPOINTS.earlyAccess.connectYouTube);
  }

  async finalizeEarlyAccessYouTube(code: string) {
    return this.request<any>(
      API_ENDPOINTS.earlyAccess.finalizeYouTube,
      {
        method: "POST",
        body: JSON.stringify({ code }),
      }
    );
  }

  // Collaboration
  async addCollaborator(projectId: string, email: string, permission: "view" | "comment" | "edit", task_id?: string, task_name?: string, task_description?: string, roles?: string[]) {
    return this.request(API_ENDPOINTS.collaboration.addCollaborator(projectId), {
      method: "POST",
      body: JSON.stringify({ email: email.toLowerCase(), permission, task_id, task_name, task_description, roles }),
    });
  }

  async removeCollaborator(projectId: string, userId: string) {
    return this.request(API_ENDPOINTS.collaboration.removeCollaborator(projectId, userId), {
      method: "DELETE",
    });
  }

  async updateCollaboratorPermission(projectId: string, userId: string, permission: "view" | "comment" | "edit") {
    return this.request(API_ENDPOINTS.collaboration.updateCollaborator(projectId, userId), {
      method: "PATCH",
      body: JSON.stringify({ permission }),
    });
  }

  async updateCollaboratorRoles(projectId: string, roles: string[]) {
    // Update current user's roles in the project
    return this.request(API_ENDPOINTS.collaboration.updateCollaborator(projectId, "me"), {
      method: "PATCH",
      body: JSON.stringify({ roles }),
    });
  }

  async addStickyNote(projectId: string, recommendationId: string, content: string, position?: { x: number; y: number }) {
    return this.request(API_ENDPOINTS.collaboration.addStickyNote(projectId), {
      method: "POST",
      body: JSON.stringify({ recommendation_id: recommendationId, content, position }),
    });
  }

  async updateStickyNote(projectId: string, noteId: string, content: string, position?: { x: number; y: number }) {
    return this.request(API_ENDPOINTS.collaboration.updateStickyNote(projectId, noteId), {
      method: "PATCH",
      body: JSON.stringify({ content, position }),
    });
  }

  async deleteStickyNote(projectId: string, noteId: string) {
    return this.request(API_ENDPOINTS.collaboration.deleteStickyNote(projectId, noteId), {
      method: "DELETE",
    });
  }

  async getProjectCollaboration(projectId: string) {
    return this.request(API_ENDPOINTS.collaboration.getProjectCollaboration(projectId));
  }

  async checkUserPermission(projectId: string) {
    return this.request(API_ENDPOINTS.collaboration.checkPermission(projectId));
  }

  // Analytics
  async getAnalyticsOverview() {
    return this.request<AnalyticsOverview>(API_ENDPOINTS.analytics.overview);
  }

  async getProjectAnalytics(projectId: string) {
    return this.request<ProjectAnalytics>(API_ENDPOINTS.analytics.project(projectId));
  }

  // Complete Tactics methods
  async markTacticsAsViewed(projectId: string, tacticIds: string[]) {
    return this.request(API_ENDPOINTS.tacticsComplete.markViewed(projectId), {
      method: "POST",
      body: JSON.stringify(tacticIds),
    });
  }

  async markRecommendationsAsViewed(projectId: string, recommendationIds: string[]) {
    // Legacy method for backwards compatibility - could map to existing artie score endpoints
    return this.request(`/api/v1/artie-score/mvp/project/${projectId}/recommendations/mark-viewed`, {
      method: "POST",
      body: JSON.stringify(recommendationIds),
    });
  }

  async getTacticsDefinitions(dimension?: string) {
    const params = dimension ? `?dimension=${dimension}` : '';
    return this.request(`${API_ENDPOINTS.tacticsComplete.definitions}${params}`);
  }

  async goDeeperTacticsChat(projectId: string, tacticId: string, message: string) {
    return this.request(API_ENDPOINTS.tacticsComplete.goDeeper(projectId, tacticId), {
      method: "POST",
      body: JSON.stringify({ message }),
    });
  }

  async acceptTactics(projectId: string, tacticId: string) {
    return this.request(API_ENDPOINTS.tacticsComplete.accept(projectId, tacticId), {
      method: "POST",
    });
  }

  async declineTactics(projectId: string, tacticId: string, reason?: string) {
    return this.request(API_ENDPOINTS.tacticsComplete.decline(projectId, tacticId), {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  }

  async completeTactics(projectId: string, tacticId: string, completionMethod: string, artifactUrls?: string[], planDescription?: string) {
    return this.request(API_ENDPOINTS.tacticsComplete.complete(projectId, tacticId), {
      method: "POST",
      body: JSON.stringify({
        completion_method: completionMethod,
        artifact_urls: artifactUrls,
        plan_description: planDescription,
      }),
    });
  }

  // Tactics V2 methods
  async getTacticsV2Recommendations(projectId: string) {
    return this.request<{
      project_id: string;
      artie_score: number;
      creator_phase: string;
      recommendations: Array<{
        tactic_id: string;
        tactic_name: string;
        dimension: string;
        priority_score: number;
        impact_tier: string;
        actionability_tier: string;
        status: string;
        reason: string;
        templates: {
          what_to_improve: string;
          why_it_matters: string;
          how_to_improve: string;
        };
        created_at: string;
        first_viewed_at?: string;
        accepted_at?: string;
        completed_at?: string;
        declined_at?: string;
      }>;
      suppression_reason?: string;
      total_eligible_tactics: number;
      generated_at: string;
    }>(API_ENDPOINTS.tacticsV2.recommendations(projectId));
  }

  async acceptTacticsV2(projectId: string, tacticId: string) {
    return this.request(API_ENDPOINTS.tacticsV2.accept(projectId), {
      method: "POST",
      body: JSON.stringify({ tactic_id: tacticId }),
    });
  }

  async declineTacticsV2(projectId: string, tacticId: string, reason?: string) {
    return this.request(API_ENDPOINTS.tacticsV2.decline(projectId), {
      method: "POST",
      body: JSON.stringify({ tactic_id: tacticId, reason }),
    });
  }

  async completeTacticsV2(projectId: string, tacticId: string, completionMethod: string, artifactUrls?: string[], planDescription?: string) {
    return this.request(API_ENDPOINTS.tacticsV2.complete(projectId), {
      method: "POST",
      body: JSON.stringify({
        tactic_id: tacticId,
        completion_method: completionMethod,
        artifact_urls: artifactUrls,
        plan_description: planDescription,
      }),
    });
  }

  async markTacticsV2AsViewed(projectId: string, tacticIds: string[]) {
    return this.request(API_ENDPOINTS.tacticsV2.markViewed(projectId), {
      method: "POST",
      body: JSON.stringify(tacticIds),
    });
  }

  // Invitation
  async getInvitationDetails(token: string) {
    return this.request<InvitationDetails>(`/api/v1/auth/invitation/${token}`);
  }

  async acceptInvitation(token: string, roles?: string[]) {
    return this.request(`/api/v1/auth/invitation/${token}/accept`, {
      method: "POST",
      body: JSON.stringify({ roles }),
    });
  }

  // Brand Onboarding
  async brandSignup(data: {
    name: string;
    email: string;
    password: string;
    turnstile_token?: string;
    website?: string; // honeypot, always empty for a person
  }) {
    const response = await this.request<{
      message: string;
      user_id: string;
      access_token: string;
      token_type: string;
      onboarding_status: string;
      next_step: string;
    }>("/api/v1/brands/signup", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (response.access_token) {
      sessionStorage.setItem('safari_auth_token', response.access_token);
    }

    return response;
  }

  async brandLogin(credentials: { email: string; password: string }) {
    const response = await this.request<{
      access_token: string;
      token_type: string;
      user: any;
      brand: any;
      onboarding_status: string;
      next_step: string;
    }>("/api/v1/brands/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    // Store token for subsequent requests since this endpoint returns it in body
    // rather than setting an HttpOnly cookie like the main auth/login endpoint
    if (response.access_token) {
      sessionStorage.setItem('safari_auth_token', response.access_token);
    }

    return response;
  }

  async setupCompanyInfo(data: {
    company_name: string;
    company_type: "direct" | "agency";
    agency_name?: string;
    website: string;
    industry_category?: string;
  }) {
    return this.request<{
      message: string;
      brand_id: string;
      onboarding_status: string;
      next_step: string;
    }>("/api/v1/brands/onboarding/company-info", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async setupBrandProfile(data: {
    brand_division?: string;
    function_role?: string;
    work_location?: string;
    business_location?: string;
    phone?: string;
    annual_marketing_budget?: string;
    annual_creator_budget?: string;
    brand_social_channels?: Array<{ platform: string; handle: string; is_primary: boolean }>;
    referral_source?: string;
    signup_reason?: string;
    logo_url?: string;
    brand_guidelines_url?: string;
  }) {
    return this.request<{
      message: string;
      onboarding_status: string;
      next_step: string;
    }>("/api/v1/brands/onboarding/brand-profile", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async completeBrandOnboarding(data: {
    ai_processing_consent: boolean;
    data_sharing_consent: boolean;
  }) {
    return this.request<{
      message: string;
      onboarding_status: string;
      brand: any;
      ready_for_campaigns: boolean;
    }>("/api/v1/brands/onboarding/complete", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getBrandOnboardingStatus() {
    return this.request<{
      user_id: string;
      brand_id?: string;
      onboarding_status: string;
      completed_steps: string[];
      next_step?: string;
      progress_percentage: number;
    }>("/api/v1/brands/onboarding/status");
  }

  async getMyBrandProfile() {
    return this.request<{
      user: any;
      brand: any;
      onboarding_status: string;
      onboarding_complete: boolean;
      progress_percentage: number;
      next_step: string;
    }>("/api/v1/brands/me/profile");
  }

  // Campaigns
  async listBookingWindows() {
    // This endpoint now returns a paginated response, but for backward compatibility in brand dashboard
    // we extract the items array.
    // Ideally, brand dashboard should also support pagination if the list grows large.
    const response = await this.request<{ items: BookingWindowSummary[] }>("/api/v1/booking-windows?limit=100");
    return response.items;
  }

  async createCampaign(data: any) {
    return this.request<any>("/api/v1/campaigns", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getBrandCampaigns(brandId: string) {
    return this.request<any[]>("/api/v1/campaigns/my-campaigns");
  }

  async getCampaign(campaignId: string) {
    return this.request<any>(`/api/v1/campaigns/${campaignId}`);
  }

  async updateCampaign(campaignId: string, data: any) {
    return this.request<any>(`/api/v1/campaigns/${campaignId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteCampaign(campaignId: string) {
    return this.request<any>(`/api/v1/campaigns/${campaignId}`, {
      method: "DELETE",
    });
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getCampaignEligibleProjects(
    campaignId: string,
    page = 1,
    limit = 100, // Increase default limit to get more projects
    search?: string,
    status?: string,
    sort?: string
  ) {
    // Build query parameters
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (search) {
      params.append('search', search);
    }
    if (status) {
      params.append('status', status);
    }
    if (sort) {
      params.append('sort', sort);
    }

    // Use the direct campaign projects endpoint which now includes ranking
    return this.request<any>(`/api/v1/campaigns/${campaignId}/projects?${params.toString()}`);
  }

  async triggerCampaignProjectRanking(campaignId: string) {
    return this.request<{
      task_id: string;
      status: string;
      message: string;
    }>(`/api/v1/campaigns/${campaignId}/projects/rank`, {
      method: 'POST'
    });
  }

  async getCampaignRankingStatus(campaignId: string, taskId: string) {
    return this.request<{
      task_id: string;
      status: 'pending' | 'progress' | 'completed' | 'failed';
      message: string;
      data?: any;
      error?: string;
      progress?: any;
    }>(`/api/v1/campaigns/${campaignId}/projects/status/${taskId}`);
  }

  async getBrandEligibleProjects(
    brandId: string,
    options: {
      profile_id?: string;
      campaign_id?: string;
      limit?: number;
      min_score?: number;
      include_ineligible?: boolean;
    } = {}
  ) {
    const params = new URLSearchParams();
    if (options.profile_id) params.append('profile_id', options.profile_id);
    if (options.campaign_id) params.append('campaign_id', options.campaign_id);
    if (options.limit) params.append('limit', String(options.limit));
    if (options.min_score !== undefined) params.append('min_score', String(options.min_score));
    if (options.include_ineligible) params.append('include_ineligible', 'true');

    return this.request<any>(`/api/v1/brand-preferences/brands/${brandId}/eligible-projects?${params.toString()}`);
  }

  async analyzeBrandProjectMatch(
    brandId: string,
    projectId: string,
    profileId?: string
  ) {
    const params = new URLSearchParams();
    if (profileId) params.append('profile_id', profileId);

    return this.request<any>(`/api/v1/brand-preferences/brands/${brandId}/project-match/${projectId}?${params.toString()}`);
  }

  async getCurrentBookingWindow() {
    return this.request<any>("/api/v1/booking-windows/current");
  }

  // Consent Management
  async createConsent(consentData: any) {
    return this.request(API_ENDPOINTS.consent.create, {
      method: "POST",
      body: JSON.stringify(consentData),
    });
  }

  async getUserConsents() {
    return this.request(API_ENDPOINTS.consent.list);
  }

  async getConsentSummary() {
    return this.request(API_ENDPOINTS.consent.summary);
  }

  async validateConsents(action: string = "basic_access") {
    return this.request(`${API_ENDPOINTS.consent.validate}?action=${action}`);
  }

  async getSpecificConsent(consentType: string) {
    return this.request(API_ENDPOINTS.consent.get(consentType));
  }

  async updateConsent(consentType: string, updateData: any) {
    return this.request(API_ENDPOINTS.consent.update(consentType), {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
  }

  async revokeConsent(consentType: string) {
    return this.request(API_ENDPOINTS.consent.revoke(consentType), {
      method: "POST",
    });
  }

  async createBulkConsents(consents: any[]) {
    return this.request(API_ENDPOINTS.consent.bulk, {
      method: "POST",
      body: JSON.stringify(consents),
    });
  }

  // Specific consent flows
  async acceptTermsOfService(scrollPercentage: number = 100, timeSpentReading: number = 0, documentVersion: string = "v1.0") {
    return this.request(`${API_ENDPOINTS.consent.termsOfService}?scroll_percentage=${scrollPercentage}&time_spent_reading=${timeSpentReading}&document_version=${documentVersion}`, {
      method: "POST",
    });
  }

  async acceptPrivacyPolicy(documentVersion: string = "v1.0") {
    return this.request(`${API_ENDPOINTS.consent.privacyPolicy}?document_version=${documentVersion}`, {
      method: "POST",
    });
  }

  async verifyAge() {
    return this.request(API_ENDPOINTS.consent.ageVerification, {
      method: "POST",
    });
  }

  async acceptAiProcessing() {
    return this.request(API_ENDPOINTS.consent.aiProcessing, {
      method: "POST",
    });
  }

  async acceptDataSharing() {
    return this.request(API_ENDPOINTS.consent.dataSharing, {
      method: "POST",
    });
  }

  async acceptYoutubeOauth(oauthScope: string) {
    return this.request(`${API_ENDPOINTS.consent.youtubeOauth}?oauth_scope=${oauthScope}`, {
      method: "POST",
    });
  }

  async acceptCampaignTerms(campaignId: string) {
    return this.request(`${API_ENDPOINTS.consent.campaignTerms}?campaign_id=${campaignId}`, {
      method: "POST",
    });
  }

  async acceptProducerAuthorization(collaboratorEmail: string, projectId?: string) {
    const params = new URLSearchParams({ collaborator_email: collaboratorEmail });
    if (projectId) params.append("project_id", projectId);

    return this.request(`${API_ENDPOINTS.consent.producerAuthorization}?${params.toString()}`, {
      method: "POST",
    });
  }

  // Brand Marketplace methods
  async getBrandMarketplacePackages(campaignId: string, page = 1, limit = 20, search?: string, status?: string) {
    const params = new URLSearchParams({
      campaign_id: campaignId,
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (status) params.append("status", status);

    return this.request(`${API_ENDPOINTS.brandMarketplace.packages}?${params.toString()}`);
  }

  async getBrandMarketplacePackageForProject(projectId: string, campaignId: string) {
    const params = new URLSearchParams({
      project_id: projectId,
      campaign_id: campaignId,
    });

    return this.request(`${API_ENDPOINTS.brandMarketplace.package}?${params.toString()}`);
  }

  async getBrandMarketplacePackageDetails(packageId: string, campaignId: string) {
    const params = new URLSearchParams({
      campaign_id: campaignId,
    });

    return this.request(`${API_ENDPOINTS.brandMarketplace.packageDetails(packageId)}?${params.toString()}`);
  }

  async holdBrandMarketplacePackage(packageId: string, campaignId: string) {
    return this.request(API_ENDPOINTS.brandMarketplace.holdPackage(packageId), {
      method: "POST",
      body: JSON.stringify({ campaign_id: campaignId }),
    });
  }

  async getBrandMarketplaceEligibleProjects(
    campaignId: string,
    page = 1,
    limit = 20,
    search?: string,
    category?: string,
    minBudget?: number,
    maxBudget?: number
  ) {
    const params = new URLSearchParams({
      campaign_id: campaignId,
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (category) params.append("category", category);
    if (minBudget) params.append("min_budget", minBudget.toString());
    if (maxBudget) params.append("max_budget", maxBudget.toString());

    return this.request(`${API_ENDPOINTS.brandMarketplace.eligibleProjects}?${params.toString()}`);
  }

  async requestBrandMarketplaceScriptContext(campaignId: string, projectId: string, requestData: any) {
    return this.request(API_ENDPOINTS.brandMarketplace.requestScriptContext(campaignId, projectId), {
      method: "POST",
      body: JSON.stringify(requestData),
    });
  }

  async recordBrandMarketplacePreferenceSignal(campaignId: string, projectId: string, signal: number) {
    return this.request(API_ENDPOINTS.brandMarketplace.preferenceSignals(campaignId), {
      method: "POST",
      body: JSON.stringify({
        project_id: projectId,
        signal: signal,
      }),
    });
  }

  async getBrandMarketplacePreferenceSignals(campaignId: string) {
    return this.request(API_ENDPOINTS.brandMarketplace.preferenceSignals(campaignId));
  }
}

// Create and export API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// Export types for better TypeScript support
export interface PitchDeckSection {
  section_type: string;
  title: string;
  content: string;
  order: number;
}

export interface ApiError {
  detail: string;
  code?: string;
}

export interface ProjectListResponse {
  projects: Project[];
  total: number;
  page: number;
  limit: number;
}

export interface Project {
  _id: string;
  user_id: string;
  title: string;

  // New fields
  logline?: string;
  same_as_prior?: boolean;
  format?: "Vertical Video" | "Long form Video" | "Feature Length Film" | "Podcast & Audio" | string;
  episode_count?: number;
  help_needed?: string[];

  description?: string;
  status:
  | "draft"
  | "pending"
  | "context_complete"
  | "script_uploaded"
  | "analysis_in_progress"
  | "analysis_complete"
  | "negotiation"
  | "deal_closed"
  | "processing";

  // Collaboration fields
  collaborators?: Array<{
    user_id: string;
    user_email: string;
    user_name?: string;
    permission: "view" | "comment" | "edit";
    task_ids?: string[];
    added_at: string;
    added_by: string;
  }>;
  sticky_notes?: Array<{
    id: string;
    recommendation_id: string;
    user_id: string;
    user_name: string;
    content: string;
    position?: { x: number; y: number };
    created_at: string;
    updated_at: string;
  }>;
  collaboration_activity?: Array<{
    action: string;
    user_id: string;
    user_name: string;
    timestamp: string;
    details?: Record<string, any>;
  }>;
  budget?: number;
  brand_revenue_target?: number;
  flexibility_preference?: "very_flexible" | "flexible_with_limits" | "selective";
  flexibility_details?: string;
  existing_financing?: number;
  distribution_strategy?: string[];
  genres?: string[];
  sub_genre?: string;
  demographics?: string[];
  audience_profiles?: string[];
  territories?: string[];
  target_30d_reach_band?: string;
  launch_date?: string;
  production_date?: string;
  post_production_date?: string;
  bid_deadline?: string;
  fulfillment_deadline?: string;
  creative_deadline?: string;
  integration_types?: string[];
  optimization_goal?: string;
  target_revenue?: number;
  target_logo_count?: number;
  target_product_use_count?: number;
  target_product_mention_count?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Legacy properties for Dashboard compatibility
  genre?: string | string[];
  target_reach?: string;
  sponsorship_potential?: number;
  confidence_band?: "low" | "mid" | "high";

  // Production Readiness fields
  shoot_dates?: string;
  release_dates?: string;
  budget_funded_amount?: number;
  team_members?: Array<{
    name: string;
    role: string;
    bio?: string;
    past_work?: string;
  }>;
  locations?: string[];
  equipment_list?: string[];
  permits?: string[];
  brand_work_portfolio?: Array<{
    brand_name: string;
    project_title: string;
    contract_link?: string;
    brief_link?: string;
    video_link?: string;
    description?: string;
  }>;
  production_credits?: string[];
  insurance_coverage?: {
    has_coverage: boolean;
    type?: string;
    provider?: string;
    coverage_amount?: number;
    policy_number?: string;
  };

  // Admin fields
  is_reviewed?: boolean;
  artie_score?: any;
  assets?: ProjectAsset[];
}

export interface ProjectAsset {
  id: string;
  filename: string;
  original_filename: string;
  s3_key: string;
  url: string;
  content_type: string;
  size: number;
  width: number;
  height: number;
  uploaded_at: string;
  uploaded_by: string;
  deleted_at?: string;
}

// Script-related interfaces
export type ScriptStatus = "pending_upload" | "uploaded" | "processing" | "analyzed" | "analysis_complete" | "error";

export type CommercialQueryType =
  | "product_placement"
  | "brand_mention"
  | "sponsored_content"
  | "advertisement"
  | "endorsement";

export interface CommercialQuery {
  id: string;
  query_type: CommercialQueryType;
  content: string;
  context: string;
  confidence_score: number;
  page_number?: number;
  line_number?: number;
  suggested_budget_impact?: number;
  notes?: string;
  reviewed: boolean;
  approved: boolean;
  created_at: string;
  updated_at: string;
}

export interface BudgetCalculation {
  total_estimated_cost: number;
  breakdown_by_type: Record<string, number>;
  approved_queries_cost: number;
  pending_queries_cost: number;
  calculation_method: string;
  calculation_notes?: string;
  last_calculated: string;
}

export interface Script {
  _id: string;
  id: string;
  script_id?: string; // Explicit fallback ID
  project_id: string;
  user_id: string;
  title?: string;
  filename: string;
  file_size: number;
  file_type: string;
  storage_path?: string;
  extracted_text?: string;
  page_count?: number;
  word_count?: number;
  status: ScriptStatus;
  commercial_queries: CommercialQuery[];
  budget_calculation?: BudgetCalculation;
  analysis_started_at?: string;
  analysis_completed_at?: string;
  analysis_error?: string;
  ai_model_used?: string;
  ai_analysis_cost?: number;
  created_at: string;
  updated_at: string;
}

export interface ScriptPreview {
  script_id: string;
  filename: string;
  preview: string;
  total_chars: number;
  word_count?: number;
  page_count?: number;
}

export interface ScriptBudget {
  script_id: string;
  filename: string;
  budget_calculation: BudgetCalculation;
  commercial_queries: Array<{
    id: string;
    type: CommercialQueryType;
    content: string;
    suggested_budget_impact?: number;
    approved: boolean;
    reviewed: boolean;
    confidence_score: number;
  }>;
  summary: {
    total_queries: number;
    approved_queries: number;
    pending_queries: number;
    high_confidence_queries: number;
  };
}

export interface ScriptStatistics {
  total_scripts: number;
  status_breakdown: Record<ScriptStatus, number>;
  budget_estimates: {
    total_budget: number;
    approved_budget: number;
  };
}

export interface ShareLink {
  _id: string;
  share_token: string;
  artie_score_id: string;
  project_id: string;
  user_id: string;
  is_active: boolean;
  expires_at?: string;
  view_count: number;
  unique_viewers: string[];
  signup_conversions: number;
  converted_user_ids: string[];
  created_at: string;
  updated_at: string;
  last_viewed_at?: string;
}

export interface ShareLinkResponse extends ShareLink {
  share_url: string;
}

export interface PublicScorePreview {
  project_id: string;
  project_title: string;
  artie_score: number;
  score_label: string;
  budget_viability: string;
  reach_status: string;
  brand_fit_status: string;
  production_readiness_status: string;
  creator_name?: string;
  creator_avatar?: string;
  created_at: string;
  is_authenticated: boolean;
  full_score_data?: any; // Complete Artie Score data for authenticated users
}

export const getScriptAnalysis = (scriptId: string): Promise<ScriptAnalysis> => {
  return apiClient.getScriptAnalysis(scriptId);
};

// Analytics types
export interface AnalyticsOverview {
  user_id: string;
  generated_at: string;
  projects: {
    total: number;
    by_status: Record<string, number>;
    recent_count: number;
  };
  scores: {
    total: number;
    average_score: number;
    distribution: {
      early_stage: number;
      in_progress: number;
      strong_foundation: number;
      ready_for_market: number;
    };
    recent_count: number;
    trends: Array<{
      month: string;
      average_score: number;
      count: number;
    }>;
  };
  traffic_lights: {
    reach: { green: number; yellow: number; red: number };
    brand_fit: { green: number; yellow: number; red: number };
    production_readiness: { green: number; yellow: number; red: number };
  };
  recommendations: {
    total: number;
    by_status: Record<string, number>;
    completion_rate: number;
  };
  budget: {
    total_target: number;
    viability_distribution: Record<string, number>;
  };
  collaboration: {
    total_collaborators: number;
    total_sticky_notes: number;
    projects_with_collaborators: number;
  };
  channels: {
    social_profiles: Array<{
      platform: string;
      handle: string;
      follower_count?: number;
      avg_views?: number;
      total_likes?: number;
      url?: string;
      profile_photo?: string;
      core_performance?: Record<string, any>;
      engagement_retention?: Record<string, any>;
      demographics?: Record<string, any>;
      traffic_discovery?: Record<string, any>;
    }>;
    youtube_channel?: {
      channel_id: string;
      title: string;
      custom_url?: string;
      thumbnail_url?: string;
      country?: string;
      stats?: {
        subscriber_count: number;
        view_count: number;
        video_count: number;
      };
      last_updated?: string;
    };
    total_channels: number;
  };
}

export interface ProjectAnalytics {
  project_id: string;
  project_title: string;
  project_status: string;
  score_history: Array<{
    date: string;
    artie_score: number;
    reach_status: string;
    brand_fit_status: string;
    production_readiness_status: string;
  }>;
  latest_score: {
    artie_score: number;
    score_label: string;
    budget_viability: string;
    created_at: string;
  } | null;
  recommendation_progress: Array<{
    id: string;
    title: string;
    status: string;
    priority: number;
    related_metric: string;
    accepted_at: string | null;
    completed_at: string | null;
  }>;
  collaboration_activity: Array<any>;
  collaborators_count: number;
  sticky_notes_count: number;
}
